import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "../lib/api";
import { CATEGORY_META, getCategory } from "../lib/constants";

/**
 * Search autocomplete dropdown shown under any Lokaso search input.
 *
 * Triggers after 2+ characters, debounced 250ms, returns up to 6 results in
 * two groups: matching businesses and matching categories.
 *
 * Keyboard nav: Up / Down arrows + Enter to select. Esc closes.
 */
export default function SearchAutocomplete({ query, onClose, onSelect, citySlug = "", variant = "panel" }) {
  const [items, setItems] = useState({ businesses: [], categories: [] });
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  // Build flat list for keyboard nav
  const flat = [
    ...items.categories.map((c) => ({ type: "category", slug: c.slug, label: c.name, meta: c })),
    ...items.businesses.map((b) => ({ type: "business", slug: b.slug, label: b.name, meta: b })),
  ];

  // Debounced fetch
  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 2) {
      setItems({ businesses: [], categories: [] });
      return;
    }
    const ql = q.toLowerCase();
    // Match categories synchronously
    const cats = Object.entries(CATEGORY_META)
      .filter(([slug, m]) => slug.includes(ql) || m.name.toLowerCase().includes(ql))
      .slice(0, 3)
      .map(([slug, m]) => ({ slug, name: m.name, emoji: m.emoji, color: m.color }));

    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const params = { q, limit: 6, page: 1, sort: "rating" };
        if (citySlug) params.city = citySlug;
        const { data } = await api.get("/businesses", { params });
        setItems({
          businesses: (data.items || []).slice(0, 6 - cats.length),
          categories: cats,
        });
        setActive(0);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("[autocomplete] fetch failed:", e?.message);
        }
        setItems({ businesses: [], categories: cats });
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(handle);
  }, [query, citySlug]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (!flat.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => (a + 1) % flat.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => (a - 1 + flat.length) % flat.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        pick(flat[active]);
      } else if (e.key === "Escape") {
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const pick = (item) => {
    if (!item) return;
    onSelect?.(item);
    if (item.type === "category") {
      navigate(`/search?category=${item.slug}${citySlug ? `&city=${citySlug}` : ""}`);
    } else {
      navigate(`/business/${item.slug}`);
    }
    onClose?.();
  };

  if ((query || "").trim().length < 2) return null;
  const totalCount = flat.length;
  if (!loading && totalCount === 0) return null;

  return (
    <div
      ref={wrapRef}
      data-testid="search-autocomplete"
      className={`absolute left-0 right-0 top-full mt-2 z-50 bg-white text-lokaso-dark rounded-2xl border border-stone-200 shadow-2xl overflow-hidden ${variant === "hero" ? "" : ""}`}
    >
      {loading && (
        <div className="px-4 py-3 text-sm text-stone-500 flex items-center gap-2">
          <Search className="w-4 h-4 animate-pulse" /> Recherche…
        </div>
      )}

      {items.categories.length > 0 && (
        <div className="py-2">
          <div className="px-4 py-1 text-[11px] uppercase tracking-wider font-semibold text-stone-400">
            Catégories
          </div>
          <ul>
            {items.categories.map((c, i) => {
              const idx = i;
              const isActive = idx === active;
              return (
                <li key={`c-${c.slug}`}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => pick({ type: "category", slug: c.slug, label: c.name, meta: c })}
                    data-testid={`autocomplete-cat-${c.slug}`}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${
                      isActive ? "bg-lokaso-cream" : "hover:bg-stone-50"
                    }`}
                  >
                    <span
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
                      style={{ background: `${c.color}1f`, color: c.color }}
                    >
                      {c.emoji}
                    </span>
                    <span className="font-medium">{c.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {items.businesses.length > 0 && (
        <div className="py-2 border-t border-stone-100">
          <div className="px-4 py-1 text-[11px] uppercase tracking-wider font-semibold text-stone-400">
            Entreprises
          </div>
          <ul>
            {items.businesses.map((b, i) => {
              const idx = items.categories.length + i;
              const isActive = idx === active;
              const cat = getCategory(b.category_slug);
              return (
                <li key={`b-${b.id || b.slug}`}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => pick({ type: "business", slug: b.slug, label: b.name, meta: b })}
                    data-testid={`autocomplete-biz-${b.slug}`}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${
                      isActive ? "bg-lokaso-cream" : "hover:bg-stone-50"
                    }`}
                  >
                    <span
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-lg shrink-0"
                      style={{ background: `${cat.color}1f`, color: cat.color }}
                    >
                      {cat.emoji}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{b.name}</p>
                      <p className="text-xs text-stone-500 truncate">
                        {b.neighborhood_name || cat.name}
                        {b.city_slug ? ` · ${b.city_slug.charAt(0).toUpperCase() + b.city_slug.slice(1)}` : ""}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
